"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ContextMenuMobile = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const vaul_1 = require("vaul");
const DialogPrimitive = tslib_1.__importStar(require("@radix-ui/react-dialog"));
const context_1 = require("../../../styles/context");
const context_2 = require("../../Popup/context");
const ArgsPane_1 = require("../ArgsPane");
const portal_1 = require("../../../utils/portal");
const MobileMenuPane_1 = require("./MobileMenuPane");
const vaulCss_1 = require("./vaulCss");
const styles_1 = require("./styles");
/** Closes every level of the sheet up to and including the root. Wired by the root pane. */
const CloseAllContext = React.createContext(() => { });
/**
 * `true` while the parent `<PaneSheet>` is open. When the root requests a
 * close-all, this flips `false` and propagates down so every nested level
 * also closes — they animate down together rather than the root vanishing
 * out from under a still-mounted child.
 */
const ParentOpenContext = React.createContext(true);
/**
 * The DOM element passed as Vaul's `container` so each level's Drawer.Portal
 * mounts inside our in-house `<Portal>`. Without this every nested level
 * would mount to `document.body` (Vaul's default) and click-away walks in an
 * ancestor `<Popup>` would not recognize the nested drawer as "inside" — the
 * popup would close on every tap.
 */
const ContainerContext = React.createContext(null);
/**
 * Mobile-friendly bottom-sheet replacement for `<ContextMenu>`. Renders the
 * same `MenuItem` tree as the desktop variant but as a Vaul bottom sheet,
 * with submenus opening as Vaul nested drawers stacked on top.
 */
const ContextMenuMobile = ({ menu: rootMenu }) => {
    const popup = (0, context_2.usePopup)();
    React.useLayoutEffect(() => {
        (0, vaulCss_1.ensureVaulCss)();
    }, []);
    const [open, setOpen] = React.useState(false);
    React.useEffect(() => {
        const id = requestAnimationFrame(() => setOpen(true));
        return () => cancelAnimationFrame(id);
    }, []);
    const closeAll = React.useCallback(() => {
        setOpen(false);
    }, []);
    const handleRootOpenChange = React.useCallback((next) => {
        setOpen(next);
    }, []);
    const handleRootAnimationEnd = React.useCallback((isOpen) => {
        if (!isOpen)
            popup?.close();
    }, [popup]);
    // Mount through the in-house `<Portal>` so that taps inside the sheet are
    // recognized as "inside" by `useClickAway` (which walks portal roots). This
    // prevents the parent `<Popup>`'s click-away handler from closing the sheet
    // on every interaction.
    const [container, setContainer] = React.useState(null);
    const setContainerRef = React.useCallback((el) => {
        setContainer(el);
    }, []);
    return (React.createElement(portal_1.Portal, null,
        React.createElement("div", { ref: setContainerRef }),
        container && (React.createElement(CloseAllContext.Provider, { value: closeAll },
            React.createElement(ContainerContext.Provider, { value: container },
                React.createElement(PaneSheet, { menu: rootMenu, open: open, onOpenChange: handleRootOpenChange, onAnimationEnd: handleRootAnimationEnd, isRoot: true }))))));
};
exports.ContextMenuMobile = ContextMenuMobile;
const PaneSheet = ({ menu, parent, open, onOpenChange, onAnimationEnd, isRoot }) => {
    const styles = (0, context_1.useStyles)();
    const closeAll = React.useContext(CloseAllContext);
    const parentOpen = React.useContext(ParentOpenContext);
    const container = React.useContext(ContainerContext);
    const [nested, setNested] = React.useState(null);
    const [nestedOpen, setNestedOpen] = React.useState(false);
    const effectiveOpen = open && parentOpen;
    const handlePushMenu = React.useCallback((item) => {
        setNested({ kind: 'menu', item });
        requestAnimationFrame(() => setNestedOpen(true));
    }, []);
    const handlePushArgs = React.useCallback((item) => {
        setNested({ kind: 'args', item });
        requestAnimationFrame(() => setNestedOpen(true));
    }, []);
    const handleNestedOpenChange = React.useCallback((next) => {
        setNestedOpen(next);
    }, []);
    const handleNestedAnimationEnd = React.useCallback((isOpen) => {
        if (!isOpen)
            setNested(null);
    }, []);
    const closeButtonRef = React.useRef(null);
    const requestBack = React.useCallback(() => {
        closeButtonRef.current?.click();
    }, []);
    // Slate / contenteditable hosts (mu-txt) close their floaters when focus
    // leaves the editor. The default mousedown behavior on a non-editable
    // element blurs the contenteditable, so we suppress it on the sheet —
    // except on inputs where we genuinely need focus.
    const handleSheetMouseDown = React.useCallback((event) => {
        const tag = event.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA')
            return;
        event.preventDefault();
    }, []);
    const contentRef = React.useRef(null);
    const childIsOpen = nested !== null && nestedOpen;
    React.useEffect(() => {
        const el = contentRef.current;
        if (!el)
            return;
        el.style.transition = 'transform .5s cubic-bezier(.32,.72,0,1)';
        el.style.transformOrigin = 'center top';
        el.style.transform = childIsOpen ? 'scale(.95) translateY(-12px)' : '';
    }, [childIsOpen]);
    const sheetBg = styles.bg + '';
    const fgColor = styles.g(0.15);
    const sheetClassName = (0, styles_1.sheetClass)({
        bg: sheetBg,
        col: fgColor,
        bxsh: styles.light ? '0 -8px 32px rgba(0,0,0,.18)' : '0 -8px 32px rgba(0,0,0,.5)',
    });
    const RootCmp = isRoot ? vaul_1.Drawer.Root : vaul_1.Drawer.NestedRoot;
    return (React.createElement(RootCmp, { open: effectiveOpen, onOpenChange: onOpenChange, onAnimationEnd: onAnimationEnd, direction: "bottom", modal: isRoot, shouldScaleBackground: false, container: container ?? undefined },
        React.createElement(vaul_1.Drawer.Portal, null,
            React.createElement(vaul_1.Drawer.Overlay, { className: styles_1.overlayClass }),
            React.createElement(vaul_1.Drawer.Content, { ref: contentRef, className: sheetClassName, "aria-describedby": undefined, onMouseDown: handleSheetMouseDown },
                React.createElement(vaul_1.Drawer.Title, { className: styles_1.visuallyHiddenClass }, menu.name),
                React.createElement(DialogPrimitive.Close, { ref: closeButtonRef, className: styles_1.visuallyHiddenClass, "aria-hidden": "true", tabIndex: -1 }),
                React.createElement("div", { className: styles_1.handleAreaClass },
                    React.createElement(vaul_1.Drawer.Handle, null)),
                React.createElement("div", { className: (0, styles_1.paneClass)({ bg: sheetBg }) },
                    React.createElement(MobileMenuPane_1.MobileMenuPane, { menu: menu, parent: parent, onPush: handlePushMenu, onBack: requestBack, onClose: closeAll, onSelectArgs: handlePushArgs })))),
        nested && (React.createElement(ParentOpenContext.Provider, { value: effectiveOpen }, nested.kind === 'menu' ? (React.createElement(PaneSheet, { menu: nested.item, parent: menu, open: nestedOpen, onOpenChange: handleNestedOpenChange, onAnimationEnd: handleNestedAnimationEnd })) : (React.createElement(ArgsSheet, { item: nested.item, open: nestedOpen, onOpenChange: handleNestedOpenChange, onAnimationEnd: handleNestedAnimationEnd }))))));
};
const ArgsSheet = ({ item, open, onOpenChange, onAnimationEnd }) => {
    const styles = (0, context_1.useStyles)();
    const closeAll = React.useContext(CloseAllContext);
    const parentOpen = React.useContext(ParentOpenContext);
    const container = React.useContext(ContainerContext);
    const effectiveOpen = open && parentOpen;
    const sheetBg = styles.bg + '';
    const fgColor = styles.g(styles.light ? 0.15 : 0.1);
    const sheetClassName = (0, styles_1.sheetClass)({
        bg: sheetBg,
        col: fgColor,
        bxsh: styles.light ? '0 -8px 32px rgba(0,0,0,.18)' : '0 -8px 32px rgba(0,0,0,.5)',
    });
    const handleSheetMouseDown = React.useCallback((event) => {
        const tag = event.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA')
            return;
        event.preventDefault();
    }, []);
    return (React.createElement(vaul_1.Drawer.NestedRoot, { open: effectiveOpen, onOpenChange: onOpenChange, onAnimationEnd: onAnimationEnd, direction: "bottom", shouldScaleBackground: false, container: container ?? undefined },
        React.createElement(vaul_1.Drawer.Portal, null,
            React.createElement(vaul_1.Drawer.Overlay, { className: styles_1.overlayClass }),
            React.createElement(vaul_1.Drawer.Content, { className: sheetClassName, "aria-describedby": undefined, onMouseDown: handleSheetMouseDown },
                React.createElement(vaul_1.Drawer.Title, { className: styles_1.visuallyHiddenClass }, item.name),
                React.createElement("div", { className: styles_1.handleAreaClass },
                    React.createElement(vaul_1.Drawer.Handle, null)),
                React.createElement(ArgsPane_1.ArgsPane, { item: item, params: item.params ?? [], minWidth: 0, onCancel: () => onOpenChange(false), onSubmit: (list, map) => {
                        item.onSubmit?.(list, map);
                        closeAll();
                    } })))));
};
